<template>
  <div class="car-content car-main-tab-content car-reduced-width" data-js="booking-page" :class="{'car-single__service': isSingleService}">
    <spinner v-if="loading" class="car-spinner car-main-content-spinner" />

    <div v-else-if="requestError" class="car-error-message">{{ $t('errors.bookingError') }}</div>

    <div v-else-if="!displayPostponeAppointment && !displayAppointment" class="car-booking car-booking-details">
      <booking-description data-js="quote-description" class="" :booking="booking"/>

      <div class="car-offer-details">
        <div class="car-booking-status" v-if="booking.status == 'CANCELLED' || booking.status == 'FINISHED'">
          {{ $t(`booking.status.${booking.status.toLowerCase()}`) }}
        </div>

        <time-slot class="car-offer-timeslot-not-selectable" :timeslot="form.selectedTimeSlot" :is-appointment="canUseCapacityAppointments" />

        <div class="car-selected-services">
          <span class="car-label">
            {{$t('quote.serviceLabel')}}
          </span>
          <offer-service-selection
            :setLoading="updateLoading"
            :sections="selectedServices"
            :payloadkey="booking.key"
            dispatch-path="offer/getLatestBookingVersion"
            :is-disabled="booking.status !== 'OFFERED'"
            :hide-remove-icon="true"
            :display-all="booking.status === 'OFFERED'"
            :allSections="booking.latest.bookedOfferDetail.actualSections.sections"
            :hide-prices="hideCalculation" />
        </div>

        <div class="car-selected-additional-services" v-if="isAdditionalServicesAvailable">
        <span class="car-label">{{$t('quote.additionalService')}}</span>
          <offer-service-selection
            :setLoading="updateLoading"
            :sections="additionalServices"
            :payloadkey="booking.key"
            dispatch-path="offer/getLatestBookingVersion"
            :is-disabled="booking.status !== 'OFFERED'"
            :display-all="booking.status === 'OFFERED'"
            :hide-remove-icon="true"
            :allSections="booking.latest.bookedOfferDetail.actualSections.sections"
            :hide-prices="hideCalculation"
            :v-if="isAdditionalServicesAvailable" />
        </div>

        <div class="car-price-details">
          <div class="car-items">
            <div class="car-item">
              <accordion v-if="!hideCalculation" :logo="true" class="" :label="$t('quote.calculation.calculation')" :expand="false"
                     data-js="booking_calculation">
                <offer-services-review
                  data-js="detail__offer--review"
                  :bookingServices="booking.bookingServices"
                  :valuatedVehicle="booking.searchConfig.vehicleConfig"
                  :selectedOffer="booking.latest.bookedOfferDetail"
                  :selectedServices="booking.searchConfig.services"
                  :actualNetRate="booking.latest.bookedOfferDetail.actualNetPrice"
                  :actualGrossRate="booking.latest.bookedOfferDetail.actualGrossPrice"
                  :regularGrossRate="booking.latest.bookedOfferDetail.regularGrossPrice"
                  :discountedNetPrice="booking.latest.bookedOfferDetail.discountedNetPrice"
                  :discountedGrossPrice="booking.latest.bookedOfferDetail.discountedGrossPrice"
                  :maintenancePositions="maintenancePositions"/>
              </accordion>
            </div>
          </div>
        </div>

        <div v-if="!hideCalculation" class="car-offer-price">
          <div class="car-items">
            <div class="car-item">
              <span class="car-label">{{ $t('quote.price', { tax: booking.latest.bookedOfferDetail.salesTax }) }}</span>
              <span class="car-price" data-js="booking_price--container">{{ booking.latest.bookedOfferDetail.discountedGrossPrice | currency }}</span>
            </div>
          </div>
        </div>
        <div class="car-buttons">
          <primary-btn v-if="shouldShowStatus('BOOKED') && !form.selectedTimeSlot && canUseCapacityAppointments"
                       class="car-btn-primary"
                       @click="toggleAppointment()">
            {{$t('offer.appointment.setAppointmentBtn')}}
          </primary-btn>
          <primary-btn v-else-if="shouldShowStatus('BOOKED') && !form.selectedTimeSlot"
                       class="car-btn-primary"
                       @click="toggleAppointment()">
            {{$t('offer.appointment.button')}}
          </primary-btn>

          <primary-btn v-if="shouldShowStatus('BOOKED') && form.selectedTimeSlot && canUseCapacityAppointments"
                       class="car-btn-primary"
                       @click="toggleAppointment()">
            {{$t('offer.appointment.changeAppointment')}}
          </primary-btn>
          <primary-btn v-else-if="shouldShowStatus('BOOKED') && form.selectedTimeSlot"
                       class="car-btn-primary"
                       @click="toggleAppointment()">
            {{$t('offer.appointment.change')}}
          </primary-btn>

          <primary-btn v-if="isPostponeButtonAllowed"
                       class="car-btn-primary"
                       data-js="postpone__appointment"
                       @click="togglePostponeAppointment()">
            {{ $t('quote.button.postpone') }}
          </primary-btn>

          <button v-if="isRatingButtonAllowed"
                  class="car-btn car-btn-primary"
                  data-js="button__cancel"
                  @click="addRating()">
            {{$t('booking.button.rating')}}
          </button>

          <primary-btn v-if="isCreateButtonAllowed"
            @click="createBooking"
            data-js="button__booking"
            class="car-btn-primary"
            id="booking-btn">
            {{ $t('quote.button.booking') }}
          </primary-btn>

          <button v-if="isCancelButtonAllowed"
                  class="car-btn car-btn-default"
                  data-js="button__cancel"
                  @click="declineBooking()">
            {{ cancelBookingButtonText }}
          </button>

          <span v-if="!isSingleService"
                class="car-link"
                data-js="button__contact"
                @click="gotoMessage()">
            {{ sendMessageBookingButtonText }}
          </span>
        </div>
      </div>

      <div id="booking-start"></div>

      <div class="car-booking-form" v-if="booking.status === 'OFFERED'">
        <span class="car-subheadline">
          {{$t('customer.title')}}
        </span>

        <customer v-if="booking.status === 'OFFERED'" :form="form" :v="$v" />

        <div v-if="$v.form.$error" class="car-form-error-message">{{ $t('errors.unknown') }}</div>

        <div class="car-form-buttons car-buttons">
           <primary-btn v-if="shouldShowStatus('BOOKED') && !form.selectedTimeSlot && canUseCapacityAppointments"
                        class="car-btn-primary"
                        @click="toggleAppointment()">
            {{ $t('offer.appointment.setAppointmentBtn') }}
          </primary-btn>
          <primary-btn v-else-if="shouldShowStatus('BOOKED') && !form.selectedTimeSlot"
                       class="car-btn-primary"
                       @click="toggleAppointment()">
            {{ $t('offer.appointment.button') }}
          </primary-btn>

          <primary-btn v-if="form.selectedTimeSlot"
                       @click="createBooking"
                       data-js="button__booking"
                       class="car-btn-primary"
                       id="booking-btn">
            {{ $t('quote.button.booking') }}
          </primary-btn>

          <div class="car-form-required">* {{ $t('catalogSelection.requiredNote') }}</div>
        </div>
      </div>

      <div class="car-accordion-container">
        <accordion class="car-accordion-workshop" :label="$t('accordion.workshop')" data-js="workshop_details" :expand="false">
          <workshop-details :location-id="booking.locationId" :expand="true"/>
        </accordion>

        <accordion class="car-accordion-vehicle" :label="$t('accordion.vehicle')" v-if="!isSingleService">
          <vehicle-details :vehicle="getVehicle()"></vehicle-details>
        </accordion>

        <accordion class="car-accordion-user" :label="$t('accordion.user')" data-js="user_details" v-if="booking.status !== 'OFFERED'">
          <customer-details :booking="booking" />
        </accordion>

        <accordion class="car-accordion-user-ratings" data-js="user_ratings" :label="$t('accordion.ratings')" v-if="comments && comments.length">
          <workshop-review :location-id="booking.locationId"></workshop-review>
        </accordion>
      </div>

      <div class="car-info-box car-selection-overview" v-if="!hideCalculation">
        <ul class="car-notes">
          <li>{{$t('quote.note1')}}</li>
          <li>{{$t('quote.note2')}}</li>
          <li>{{$t('quote.note3')}}</li>
        </ul>
      </div>

      <div v-if="isAvailableQrCodeRedirect" class="car-dealer-qr-detail" data-js="dealer_qr_detail">
        <span class="car-subheadline">{{ $t('booking.qrCodeRedirectTitle') }}</span>

        <a rel="stylesheet" :href="qrCodeUrl" target="_blank" title="DSR Records">
          <qr-code :value="qrCodeUrl" />
        </a>
      </div>
    </div>

    <div v-else-if="displayPostponeAppointment && !displayAppointment" class="car-booking car-appointment car-postpone-appointment single__service">
      <div class="car-content-introduction car-reduced-width">
        <span class="car-headline">{{$t('booking.postpone.title')}}</span>
      </div>

      <span class="car-subheadline">{{$t('booking.postpone.selectNewTimeSlot')}}</span>

      <appointment-selection v-if="isSingleService"
                             :hideTitle="true"
                             :validationRules="$v"
                             :form="form" />

      <datepicker-container v-if="!shouldShowStatus('BOOKED') && !isSingleService"
                            @selectDate="selectDate"
                            :selectedDate="selectedDate"
                            :available-dates="timeslots"/>

      <v-select
        v-if="availableTimeSlotsForDate.length > 5"
        class="car-v-select"
        name="appointment"
        :value="form.selectedTimeSlot"
        :options="availableTimeSlotsForDate"
        :getOptionLabel="timeslotLabel"
        @input="selectTimeslot"
        :placeholder="$t('offer.appointment.appointmentLabel')" />
      <time-slot
        v-else
        v-for="timeslot in availableTimeSlotsForDate"
        :key="timeslot.timeslotId"
        :timeslot="timeslot"
        :style-flag="true"
        @click="selectTimeslot(timeslot)"
        :class="{'car-selected-appointment': timeslot === form.selectedTimeSlot }"
        :is-appointment="canUseCapacityAppointments" />

      <div class="car-form-buttons car-buttons">
        <primary-btn v-if="booking.status === 'BOOKED'"
                     class="car-btn-primary"
                     data-js="postpone__btn"
                     @click="postponeBooking()">
          {{ $t('quote.button.confirmPostponeBooking') }}
        </primary-btn>
        <button v-if="!isSingleService"
                class="car-btn car-btn-default"
                data-js="postpone__appointment--cancel"
                @click="togglePostponeAppointment()">
          {{ $t('quote.button.cancelPostpone') }}
        </button>
      </div>
    </div>

    <div v-else-if="!displayPostponeAppointment && displayAppointment && shouldShowStatus('BOOKED')" class="car-booking car-appointment car-confirm-offer">
      <div class="car-content-introduction car-reduced-width">
        <span class="car-headline">{{$t('offer.appointment.title')}}</span>
        <p>{{$t('offer.appointment.description')}}</p>
      </div>

      <span class="car-subheadline">{{$t('quote.selectTimeSlot')}}</span>

      <datepicker-container @selectDate="selectDate"
                            :selectedDate="selectedDate"
                            :available-dates="timeslots" />

      <div v-if="!$v.form.selectedTimeSlot.required && $v.form.selectedTimeSlot.$error" class="car-error-message">
        {{$t('offer.errors.date')}}
      </div>

      <v-select
        v-if="availableTimeSlotsForDate.length > 5"
        class="car-v-select"
        name="appointment"
        :value="form.selectedTimeSlot"
        :options="availableTimeSlotsForDate"
        :getOptionLabel="timeslotLabel"
        @input="selectTimeslot"
        :placeholder="$t('offer.appointment.appointmentLabel')" />
      <time-slot
        v-else
        v-for="timeslot in availableTimeSlotsForDate"
        :key="timeslot.timeslotId"
        :timeslot="timeslot"
        :style-flag="true"
        @click="selectTimeslot(timeslot)"
        :class="{'car-selected-appointment': timeslot === form.selectedTimeSlot }"
        :is-appointment="canUseCapacityAppointments" />

      <div v-if="shouldShowStatus('BOOKED')" class="car-form-buttons car-buttons">
        <primary-btn @click="toggleAppointment()" class="car-btn-primary">
          {{ $t('quote.button.acceptAppointment') }}
        </primary-btn>

        <button class="car-btn car-btn-default" @click="cancelTimeslotSelection()">
          {{ $t('quote.button.cancelPostpone') }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { filter, merge, find, get, has } from 'lodash';
import moment from 'moment';
import Spinner from 'vue-simple-spinner';
import bookingApiService from '@/services/bookingApiService';
import capacityApiService from '@/services/capacityApiService';
import bookingSectionService from '@/services/bookingSectionService';
import utils from '@/util/utils';
import SMP_CONFIG, { capacityId, locationId as contextLocationId } from '@/util/smp_config';

import Accordion from '@/components/Accordion';
import OfferServicesReview from '@/components/service-request/OfferServicesReview';
import VehicleDetails from '@/components/service-request/VehicleDetails';
import WorkshopReview from '@/components/service-request/WorkshopReview';
import BookingDescription from '@/components/service-request/BookingDescription';
import OfferServiceSelection from '@/components/service-request/OfferServiceSelection';
import WorkshopDetails from '@/components/service-request/WorkshopDetails';
import CustomerDetails from '@/components/service-request/CustomerDetails';
import DatepickerContainer from '@/components/Datepicker';
import TimeSlot from '@/components/TimeSlot';
import PrimaryBtn from '@/components/PrimaryBtn';
import AppointmentSelection from '@/modules/simpleService/components/AppointmentSelection';
import QrCode from '@/components/QrCode';
import Customer from '@/components/service-request/Customer';

const { required, email } = require('vuelidate/lib/validators');

export default {
  name: 'BookedOffer',
  data() {
    return {
      attrs: {
        highlight: {
          backgroundColor: '#EE3727'
        }
      },
      selectedDate: null,
      originalSelectedTimeslot: null,
      form: {
        selectedDate: '',
        selectedTimeSlot: null,
        email: '',
        firstName: '',
        lastName: '',
        phone: '',
        licensePlate: '',
        mileage: '',
        comment: ''
      },
      displayAppointment: false,
      displayPostponeAppointment: false,
      requestError: false,
      loading: false,
      timeslots: [],
      maintenancePositions: [],
      comments: [],
      display: {
        extraComments: false
      },
      bookingRequestErr: false
    }
  },
  validations() {
    let validationObj = {
      form: {
        email: {
          required,
          email
        },
        firstName: {
          required
        },
        lastName: {
          required
        },
        phone: {
          required
        }
      }
    }

    if (this.isSingleService) {
      validationObj.form = {
        selectedDate: {
          required
        },
        selectedTimeSlot: {
          required
        }
      }
    }

    validationObj.form.selectedTimeSlot = {
      required
    }

    return validationObj
  },
  methods: {
    getVehicle() {
      let vehicleConfig = this.booking.latest.searchConfig.vehicleConfig;
      vehicleConfig.selectedEquipments = filter(vehicleConfig.vehicleTypeEquipmentList, 'selected');

      return vehicleConfig
    },
    selectDate(r) {
      this.selectedDate = r;
      if (!this.canUseCapacityAppointments) {
        this.form.selectedTimeSlot = this.timeslots.find(item => moment(item.dateFrom.time).format('YYYY-MM-DD') === moment(r).format('YYYY-MM-DD'))
      }
    },
    setAvailableDates(timeslots) {
      this.timeslots = timeslots.map((timeslot) => {
        const tmpTimeslot = Object.assign({}, timeslot);
        tmpTimeslot.start = moment(timeslot.dateFrom.time).startOf('day').toDate()
        tmpTimeslot.end = moment(timeslot.dateTo.time).endOf('day').toDate()

        return tmpTimeslot;
      })
    },
    updateLoading(loading) {
      this.loading = loading
    },
    validateForm() {
      this.$v.$touch()
    },
    postponeBooking() {
      if (this.isSingleService) {
        this.validateForm();

        if (this.$v.$invalid || this.loading) {
          return;
        }

        if (this.originalSelectedTimeslot.id === this.form.selectedTimeSlot.id) {
          this.togglePostponeAppointment();
          return;
        }
      } else {
        if (this.originalSelectedTimeslot.timeslotId === this.form.selectedTimeSlot.timeslotId) {
          this.togglePostponeAppointment();
          return;
        }
      }

      let params = {
        timeslotId: this.form.selectedTimeSlot.timeslotId || this.form.selectedTimeSlot.id
      };
      this.loading = true

      return bookingApiService.postponeBooking(this.booking.key, params).then(() => {
        return this.$store.dispatch('offer/getBookingDetails', this.booking.key).then(() => {
          this.setInitSelectedTimeslot()
          this.togglePostponeAppointment()
        })
      }).finally(() => {
        this.loading = false
      })
    },
    togglePostponeAppointment() {
      this.displayPostponeAppointment = !this.displayPostponeAppointment;
      this.form.selectedTimeSlot = this.originalSelectedTimeslot;
    },
    toggleAppointment() {
      this.displayAppointment = !this.displayAppointment
    },
    createBooking() {
      this.validateForm();

      if (this.$v.$invalid || this.loading) {
        this.gotoBooking();
        return;
      }

      this.loading = true;
      this.bookingRequestErr = false;

      let booking = {
        timeslotId: this.form.selectedTimeSlot.timeslotId,
        comment: this.form.comment,
        selectedServiceIds: bookingSectionService.getSelectedServiceIds(this.selectedServices),
        user: {
          email: this.form.email,
          firstName: this.form.firstName,
          lastName: this.form.lastName,
          phone: this.form.phone
        },
        vehicle: {
          mileage: this.form.mileage,
          licensePlate: this.form.licensePlate
        }
      };

      return bookingApiService.createBookingFromQuote(booking, this.booking.key)
        .then(() => { this.$router.push({ path: `/service-request/confirm/${this.booking.key}` }) })
        .catch(() => { this.bookingRequestErr = true; })
        .finally(() => { this.loading = false; })
    },
    addRating() {
      this.$router.push({
        path: `/booking/${this.booking.key}/rating`
      })
    },
    declineBooking() {
      this.$router.push({
        path: `/booking/${this.booking.key}/cancel`
      })
    },
    getLocationVotings(locationId) {
      bookingApiService.getLocationVoting(locationId).then((res) => {
        this.comments = res.data
        // set all comments to display overview only
        for (const comment of this.comments) {
          this.$set(this.display, 'commentDetails-' + comment.id, false);
          comment.bookedOfferVotingTypeMap = merge(comment.bookedOfferVotingTypeMap, {
            DETAIL_RELIABILITY: ['0'],
            DETAIL_QUALITY: ['0'],
            DETAIL_FRIENDLINESS: ['0'],
            OVERALL_FEELING: ['0']
          });

          if (comment.comment) {
            comment.comment = comment.comment.trim();
          } else {
            comment.comment = null;
          }
        }
      });
    },
    gotoMessage() {
      if (this.serviceCase.messageAccessToken) {
        this.$router.push({ path: `/conversation/${this.serviceCase.messageAccessToken}` })
      } else {
        this.$router.push({ path: '/conversation' })
      }
    },
    gotoBooking() {
      const element = document.getElementById('booking-start');
      element.scrollIntoView(true);
    },
    shouldShowStatus(status) {
      return this.booking.nextStatuses.includes(status)
    },
    setInitSelectedTimeslot() {
      this.form.selectedTimeSlot = find(this.timeslots, { timeslotId: this.booking.timeslotId });
      this.selectedDate = this.form.selectedTimeSlot && this.form.selectedTimeSlot.start;

      if (!this.form.selectedTimeSlot && this.booking.dropoffFrom) {
        let timeslot = {
          start: moment(this.booking.dropoffFrom.time).startOf('day').toDate(),
          end: moment(this.booking.dropoffTo.time).endOf('day').toDate(),
          dateFrom: this.booking.dropoffFrom,
          dateTo: this.booking.dropoffTo,
          timeslotId: this.booking.timeslotId
        };
        this.selectedDate = moment.utc(this.booking.dropoffFrom.time).startOf('day').toDate();
        this.timeslots.unshift(timeslot);
        this.form.selectedTimeSlot = this.timeslots[0];

        if (this.isSingleService) {
          this.form.selectedDate = moment(this.booking.dropoffFrom.time).startOf('day').format('YYYY-MM-DD');

          const tmpDropOffMoment = moment(this.booking.dropoffFrom.time).format('HH:mm');
          const singleTimeslot = this.simpleCapacityTimeslots[this.form.selectedDate]?.find((slot) => moment(slot.startsAt).format('HH:mm') === tmpDropOffMoment);

          if (singleTimeslot) {
            this.form.selectedTimeSlot = singleTimeslot;
          }
        }
      }

      this.originalSelectedTimeslot = this.form.selectedTimeSlot
    },
    selectTimeslot(timeslot) {
      this.form.selectedTimeSlot = timeslot;
    },
    timeslotLabel(timeslot) {
      const getEndTime = () => {
        return timeslot.dateTo || moment(timeslot.startsAt).add(timeslot.length, 'minute');
      };

      return moment(timeslot.dateFrom || timeslot.startsAt).format('LT') + '-' + moment(getEndTime()).format('LT');
    },
    cancelTimeslotSelection() {
      this.setInitSelectedTimeslot();
      this.toggleAppointment();
    }
  },
  computed: {
    ...mapGetters('capacity', {
      simpleCapacityTimeslots: 'timeSlots'
    }),
    isSingleService() {
      return utils.isSingleService();
    },
    selectedServices() {
      return get(this, 'booking.latest.bookedOfferDetail.actualSections.sections', []).filter(service => service.serviceType !== 'ADDITIONAL')
    },
    additionalServices() {
      return get(this, 'booking.latest.bookedOfferDetail.actualSections.sections', []).filter(service => service.serviceType === 'ADDITIONAL')
    },
    booking() {
      return this.$store.getters['offer/booking']
    },
    serviceCase() {
      return this.$store.getters['serviceCase/serviceCase']
    },
    isAdditionalServicesAvailable() {
      if (this.additionalServices.length) {
        if (this.booking.status !== 'OFFERED') {
          return !!find(this.additionalServices, (o) => o.selected);
        }

        return true;
      }

      return false;
    },
    cancelBookingButtonText() {
      return this.booking.status === 'OFFERED' ? this.$t('quote.button.declineBooking') : this.$t('booking.button.cancelBooking');
    },
    sendMessageBookingButtonText() {
      return this.booking.status === 'OFFERED' ? this.$t('quote.button.sendMessage') : this.$t('booking.button.sendMessage');
    },
    isAvailableQrCodeRedirect() {
      return has(SMP_CONFIG, 'contextConfig.extended.featureConfig.qrRedirect');
    },
    qrCodeUrl() {
      return get(SMP_CONFIG, 'contextConfig.extended.featureConfig.qrRedirect') + '?bookedofferkey=' + this.booking.key;
    },
    hideCalculation() {
      return get(SMP_CONFIG, 'contextConfig.extended.featureConfig.servicePriceDisplay', 'visible') === 'not_visible';
    },
    dueDate() {
      return moment(this.booking.created.time).add(14, 'days');
    },
    isCancelButtonAllowed() {
      const dropOff = get(this.booking, 'dropoffTo.time');

      return this.shouldShowStatus('CANCELLED') && (!dropOff || moment().isBefore(moment(dropOff)));
    },
    isRatingButtonAllowed() {
      const dropOff = get(this.booking, 'dropoffTo.time');

      return (
        this.booking.status === 'BOOKED' ||
          this.booking.status === 'PLANNED' ||
          this.booking.status === 'STARTED' ||
          this.booking.status === 'FINISHED' ||
          this.booking.status === 'VEHICLE_RETURNED' ||
          this.booking.status === 'REQUESTED' ||
          this.booking.status === 'CONFIRMED' ||
          this.booking.status === 'CONSIDERED_FINISHED'
      ) &&
        dropOff && moment(dropOff).isBefore(moment());
    },
    isPostponeButtonAllowed() {
      const dropOff = get(this.booking, 'dropoffTo.time');

      return this.booking.status === 'BOOKED' && (!dropOff || moment(dropOff).isAfter(moment()));
    },
    isCreateButtonAllowed() {
      return this.shouldShowStatus('BOOKED') && this.form.selectedTimeSlot;
    },
    availableTimeSlotsForDate() {
      if (this.selectedDate) {
        if (this.canUseCapacityAppointments) {
          return this.timeslots.filter(item => moment(item.start).format('YYYY-MM-DD') === moment(this.selectedDate).format('YYYY-MM-DD'))
        }

        if (this.form.selectedTimeSlot) {
          return [this.form.selectedTimeSlot];
        }
      }

      return [];
    },
    canUseCapacityAppointments() {
      return !!capacityId && contextLocationId && contextLocationId === this.booking?.locationId;
    }
  },
  async created() {
    let bookedOfferKey = this.$route.params.key;

    if (bookedOfferKey) {
      this.loading = true;

      try {
        await this.$store.dispatch('serviceCase/getCaseByBookedOfferKey', bookedOfferKey);
        await this.$store.dispatch('offer/getBookingDetails', bookedOfferKey).then(res => {
          this.form.firstName = res.givenname;
          this.form.lastName = res.surname;
          this.form.phone = res.phone;
          this.form.email = res.emailAddress;
          this.form.licensePlate = res.licenseNumber;
          this.form.mileage = res.mileage;
          return this.getLocationVotings(this.booking.locationId)
        });

        if (this.isSingleService) {
          await this.$store.dispatch('capacity/getCampaignCapacity');
        } else {
          // If capacityId is set in Context, try to search timeslots there
          if (this.canUseCapacityAppointments) {
            try {
              await capacityApiService.listAvailableTimeslots(capacityId).then((timeslots) => {
                this.timeslots = timeslots.map((curr) => {
                  return {
                    start: moment(curr.startsAt).toDate(),
                    startsAt: curr.startsAt,
                    length: curr.length,
                    end: moment(curr.startsAt).add(curr.length, 'minutes').toDate(),
                    timeslotId: curr.id
                  }
                })
              })
            } catch (ignore) {
              // In case of failure return default timeslots
              await bookingApiService.getOfferedOfferTimeslots(bookedOfferKey).then((res) => {
                let timeslots = res.data;
                this.setAvailableDates(timeslots);
              });
            }
          } else {
            // Else use drop-off timeslots from offer
            await bookingApiService.getOfferedOfferTimeslots(bookedOfferKey).then((res) => {
              let timeslots = res.data;
              this.setAvailableDates(timeslots);
            });
          }
        }
      } catch (ignore) {
        this.requestError = true;
      }

      this.loading = false;
      this.setInitSelectedTimeslot();
    }
  },
  components: {
    Customer,
    QrCode,
    TimeSlot,
    CustomerDetails,
    WorkshopDetails,
    Accordion,
    OfferServicesReview,
    Spinner,
    WorkshopReview,
    VehicleDetails,
    BookingDescription,
    OfferServiceSelection,
    DatepickerContainer,
    AppointmentSelection,
    PrimaryBtn
  }
}
</script>
